import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { appContext, serverContext, checkIfOnline } from '../App';
import MaterialTable, { MTableToolbar } from 'material-table'
import PatchedPagination from './PatchedPagination';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import AnnotationTableToolbar from './AnnotationTableToolbar';
import { useAnnotationData } from '../customEffects';
import CreateAnnotationDlg from '../CreateAnnotationDlg';
import axios from 'axios';
import { toGeoJson } from '../utils';
import { tableIcons } from '../commonTypes';
import { useTableTheming } from './useTableTheming';


const useStyles = makeStyles(theme => ({
    root: {
        '& .MuiPaper-root': {
            paddingLeft: '1em',
            paddingRight: '1em',
        }
    },
    denseTable: {
        '& .MuiTableCell-root': {
            padding: '0.2em', // Reduced cell padding
        },
        '& .MuiTableRow-root': {
            height: '32px', // Reduced row height            
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
            padding: '0.2em', // Reduced header padding
        },
        '& .MuiToolbar-root': {
            padding: '0.2em', // Reduced toolbar padding
        },
    '& .MuiTableCell-head': {
        fontWeight: 'bold', // Ensures header text is bold
    },
    },
    importProgress: {
        marginTop: '0.5em',
    }
}));

const GEOJSON_GEOMETRY_TYPES = new Set([
    'Point',
    'MultiPoint',
    'LineString',
    'MultiLineString',
    'Polygon',
    'MultiPolygon',
    'GeometryCollection',
]);

const annotationTimestampFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});

const formatAnnotationTimestamp = value => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        throw new Error('Invalid Date');
    }

    const parts = annotationTimestampFormatter.formatToParts(date);
    const getPart = type => parts.find(part => part.type === type)?.value;

    return `${getPart('month')} ${getPart('day')} ${getPart('year')}, ${getPart('hour')}:${getPart('minute')}`;
};

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

const isValidGeoJsonGeometry = geometry => {
    if (geometry === null) {
        return true;
    }

    if (!isObject(geometry) || typeof geometry.type !== 'string') {
        return false;
    }

    if (!GEOJSON_GEOMETRY_TYPES.has(geometry.type)) {
        return false;
    }

    if (geometry.type === 'GeometryCollection') {
        return Array.isArray(geometry.geometries) && geometry.geometries.every(isValidGeoJsonGeometry);
    }

    return Object.prototype.hasOwnProperty.call(geometry, 'coordinates');
};

const isValidGeoJsonObject = geoJson => {
    if (!isObject(geoJson) || typeof geoJson.type !== 'string') {
        return false;
    }

    if (geoJson.type === 'FeatureCollection') {
        return Array.isArray(geoJson.features) && geoJson.features.every(feature => isValidGeoJsonObject(feature));
    }

    if (geoJson.type === 'Feature') {
        if (!Object.prototype.hasOwnProperty.call(geoJson, 'geometry')) {
            return false;
        }

        return isValidGeoJsonGeometry(geoJson.geometry);
    }

    return isValidGeoJsonGeometry(geoJson);
};

const normalizeImportedGeoJsonPayload = parsedData => {
    if (Array.isArray(parsedData)) {
        if (parsedData.length === 0) {
            throw new Error('Invalid file format. The GeoJSON array is empty.');
        }

        if (!parsedData.every(item => isValidGeoJsonObject(item))) {
            throw new Error('Invalid file format. The array must contain only valid GeoJSON objects.');
        }

        return parsedData;
    }

    if (!isValidGeoJsonObject(parsedData)) {
        throw new Error('Invalid file format. Expected one GeoJSON object or an array of GeoJSON objects.');
    }

    return [parsedData];
};

const exportAnnotationsToGeoJson = (annotations, toGeoJson, dispatchApp) => {
    if (!annotations || annotations.length === 0) {
        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: 'No annotations available to export.', severity: 'info' } });
        return;
    }

    try {
        const geoJsonArray = annotations.map(annotation => {
            const { _id, ...rest } = annotation; // Remove _id
            return toGeoJson(rest);
        });

        const isSingleGeoJson = geoJsonArray.length === 1;
        const exportData = isSingleGeoJson ? geoJsonArray[0] : geoJsonArray;
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const extension = isSingleGeoJson ? 'geojson' : 'json';
        link.download = `Annotations_${new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '-')}.${extension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.error('Error exporting annotations:', error);
        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: 'Error exporting annotations.', severity: 'error' } });
    }
};

const deleteAnnotations = async (annotations, server, dispatchApp) => {
    if (!annotations || annotations.length === 0) {
        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: 'No annotations selected for deletion.', severity: 'info' } });
        return;
    }

    if (!checkIfOnline(server, dispatchApp)) {
        return;
    }

    try {
        const annotationIds = [...new Set(
            annotations
                .map(annotation => annotation?._id)
                .filter(Boolean)
                .map(id => String(id).trim())
                .filter(Boolean)
        )];

        if (annotationIds.length === 0) {
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: 'No valid annotation IDs selected for deletion.', severity: 'info' } });
            return;
        }

        const response = await axios.delete(`${server.serverHost}/api/annotations`, {
            headers: { 'Authorization': `Basic ${server.token}` },
            data: { annotations: annotationIds }
        });

        const deletionResults = Array.isArray(response.data) ? response.data : [];
        const failedCount = deletionResults.filter(result => result.status === 'rejected').length;
        const successCount = deletionResults.filter(result => result.status === 'fulfilled').length;

        if (failedCount === 0 && successCount > 0) {
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `${successCount} annotation(s) deleted successfully`, severity: 'success' } });
        } else if (successCount > 0) {
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `${successCount} annotation(s) deleted, ${failedCount} failed.`, severity: 'warning' } });
        } else {
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: 'Error deleting selected annotations.', severity: 'error' } });
        }        

    } catch (error) {
        console.error("Error deleting annotations:", error);
        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Error deleting annotations: ${error.message}`, severity: 'error' } });
    }
};

const getTableActions = (annotationData, selectedRow, setCreateAnnotationDlgOpen, requestDeleteConfirmation, openImportDialog, importInProgress, dispatchApp, server, toGeoJson) => {
    const hasAnnotations = Array.isArray(annotationData) && annotationData.length > 0;
    const actions = [
        {
            icon: tableIcons.AddIcon,
            tooltip: 'Add Annotation',
            isFreeAction: true,
            onClick: event => {
                if (checkIfOnline(server, dispatchApp)) {
                    setCreateAnnotationDlgOpen({ open: true, mode: 'create' });
                }
            }
        },
        ...(hasAnnotations ? [{
            icon: tableIcons.Export,
            tooltip: 'Export / Download Annotations',
            isFreeAction: false,
            onClick: (event, data) => {
                exportAnnotationsToGeoJson(data, toGeoJson, dispatchApp)
            }
        }] : []),
        {
            icon: tableIcons.Import,
            tooltip: 'Import Annotations',
            isFreeAction: true,
            disabled: importInProgress,
            onClick: () => {
                openImportDialog();
            }
        },
        {
            icon: tableIcons.DeleteIcon,
            tooltip: 'Delete Selected Annotations',
            isFreeAction: false,
            onClick: (event, data) => {
                const selectedAnnotations = Array.isArray(data) ? data : [data].filter(Boolean);
                requestDeleteConfirmation(selectedAnnotations);
            }
        }
    ];

    if (hasAnnotations && selectedRow) {
        actions.push({
            icon: tableIcons.Edit,
            tooltip: 'Edit Annotation',
            isFreeAction: true,
            onClick: event => {
                console.log("Edit Annotation Clicked for:", selectedRow);
                if (selectedRow) {
                    setCreateAnnotationDlgOpen({ open: true, mode: 'edit' });
                }
            }
        });
        actions.push({
            icon: tableIcons.DeleteIcon,
            tooltip: 'Delete Annotation',
            isFreeAction: true,
            onClick: () => {
                console.log("Delete Annotation Clicked for:", selectedRow);
                requestDeleteConfirmation([selectedRow]);
            }
        });
    }

    return actions;
};

// Function to define columns
const getColumns = (handleHideCheckboxChange) => [
    {
        title: 'Title', field: 'title', filtering: true, width: '15%',
    },
    {
        title: 'Description', field: 'description', filtering: true
    },
    {
        title: 'Tags', field: 'tags', filtering: true, render: rowData => rowData.tags ? rowData.tags.join(', ') : ''
    },
    {
        title: 'Geometries', field: 'features', filtering: true, width: '4%', render: rowData => rowData.features ? rowData.features.length : 'N/A',
    },
    {
        title: 'Restricted', field: 'restricted', width: '4%',
        render: rowData => rowData.restricted ?
            <Tooltip title="Restricted">
                <LockOutlinedIcon style={{ fontSize: '18px', color: 'grey' }} />
            </Tooltip>
            : null
    },
    { title: 'Author', field: 'author', width: '10%', render: rowData => `${rowData.author || 'N/A'}` },
    {
        title: 'Created', field: 'creationTime', width: '15%', filtering: true, defaultSort: 'desc',
        render: rowData => {
            if (!rowData.creationTime) return 'N/A';
            try {
                return formatAnnotationTimestamp(rowData.creationTime);
            } catch (e) {
                console.error("Error formatting timestamp:", rowData.creationTime, e);
                return 'Invalid Date';
            }
        }
    },
    {
        title: 'Hide',
        field: 'hide',
        width: '4%',
        render: rowData => (
            <input
                type="checkbox"
                checked={rowData.hide}
                onChange={e => handleHideCheckboxChange(rowData._id, e.target.checked)}
            />
        )
    },
];

const AnnotationTable = props => {
    const { server: contextServer, dispatchServer } = useContext(serverContext);
    const server = props.server || contextServer;
    const hasServerCredentials = Boolean(server?.serverHost && server?.token);
    const { dispatchApp } = useContext(appContext);
    const [selectedRow, setSelectedRow] = useState(null);
    const tableTheme = useTableTheming(selectedRow);
    const [filter, setFilter] = useState(() => {
        const saved = localStorage.getItem('annotationsQueryFilter');
        const parsed = saved !== null ? JSON.parse(saved) : {};

        const f = {
            disabled: parsed.disabled || [],
            mode: parsed.mode || 'all',
        };

        return f;
    });

    const [fetchFilter, setFetchFilter] = useState({});

    const [hiddenAnnotations, setHiddenAnnotations] = useState(() => {
        const saved = localStorage.getItem('hiddenAnnotations');
        return saved ? JSON.parse(saved) : [];
    });
    const { isLoading, hasError, annotationData: rawAnnotationData } = useAnnotationData(hasServerCredentials ? server.serverHost : undefined, hasServerCredentials ? server.token : undefined, fetchFilter, server?.annotationsUpdate);
    const [annotationData, setAnnotationData] = useState([]);
    const [createAnnotationDlgOpen, setCreateAnnotationDlgOpen] = useState({ open: false, mode: 'create' });
    const [deleteConfirmState, setDeleteConfirmState] = useState({ open: false, annotations: [] });
    const [importState, setImportState] = useState({ inProgress: false, progress: 0 });
    const importFileInputRef = useRef(null);


    const handleSelectedRow = useCallback((evt, selectedRowData) => {
        setSelectedRow(selectedRowData);
        if (props.onAnnotationSelected) {
            props.onAnnotationSelected(selectedRowData);
        }
    }, [props.onAnnotationSelected]);

    useEffect(() => {
        if (props.selectedAnnotationId && annotationData?.length) {
            const match = annotationData.find(row => row._id === props.selectedAnnotationId);
            if (match) {
                setSelectedRow(match);
                handleSelectedRow(null, match); // Trigger row click logic manually
            }
        }
    }, [props.selectedAnnotationId, annotationData, handleSelectedRow]);

    useEffect(() => {

        if (filter.mode === 'area' && props.mapBoundingBox) {
            setFetchFilter({ ...filter, bbox: props.mapBoundingBox });
        } else {
            setFetchFilter({ ...filter });
        }

    }, [filter, filter.mode, props.mapBoundingBox]);

    // Handle potential errors from the hook
    useEffect(() => {
        if (hasError && hasError.error) {
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Error fetching Annotation data: ${hasError.message}`, severity: 'error' } });
        }
    }, [hasError, dispatchApp]);

    useEffect(() => {

        if (rawAnnotationData && rawAnnotationData.length > 0) {
            setAnnotationData(rawAnnotationData.map(item => ({ ...item, hide: hiddenAnnotations.includes(item._id) })));
        } else {
            setAnnotationData([]);
            setSelectedRow(null);
        }

    }, [rawAnnotationData, hiddenAnnotations]);

    const classes = useStyles();

    const handleHideCheckboxChange = (rowId, isChecked) => {

        const saved = localStorage.getItem('hiddenAnnotations');
        const currHiddenAnnotations = saved ? JSON.parse(saved) : [];
        const updatedHiddenAnnotations = isChecked
            ? [...currHiddenAnnotations, rowId]
            : currHiddenAnnotations.filter(id => id !== rowId);

        setHiddenAnnotations(updatedHiddenAnnotations);
        localStorage.setItem('hiddenAnnotations', JSON.stringify(updatedHiddenAnnotations));

        const updatedAnnotationData = annotationData.map(item =>
            item._id === rowId ? { ...item, hide: isChecked } : item
        );

        setAnnotationData(updatedAnnotationData);

        if (props.onAnnotationSelectionUpdated) {
            props.onAnnotationSelectionUpdated(updatedAnnotationData.find(item => item._id === rowId));
        }
    };


    const handleToolbarSelection = selection => {
        setFilter(prevFilter => {
            const updatedFilter = { ...prevFilter, ...selection };

            localStorage.setItem('annotationsQueryFilter', JSON.stringify(updatedFilter));
            return updatedFilter;
        });
    }

    const handleAnnotationDlgClose = () => {
        setCreateAnnotationDlgOpen({ open: false });
    }

    const requestDeleteConfirmation = useCallback((annotations) => {
        const selectedAnnotations = Array.isArray(annotations) ? annotations.filter(Boolean) : [annotations].filter(Boolean);
        if (selectedAnnotations.length === 0) {
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: 'No annotations selected for deletion.', severity: 'info' } });
            return;
        }

        setDeleteConfirmState({ open: true, annotations: selectedAnnotations });
    }, [dispatchApp]);

    const closeDeleteConfirmation = useCallback(() => {
        setDeleteConfirmState({ open: false, annotations: [] });
    }, []);

    const confirmDeleteAnnotations = useCallback(async () => {
        const annotationsToDelete = deleteConfirmState.annotations;
        closeDeleteConfirmation();
        await deleteAnnotations(annotationsToDelete, server, dispatchApp);
    }, [deleteConfirmState.annotations, closeDeleteConfirmation, server, dispatchApp]);

    const openImportDialog = useCallback(() => {
        if (!checkIfOnline(server, dispatchApp)) {
            return;
        }

        if (importFileInputRef.current) {
            importFileInputRef.current.click();
        }
    }, [server, dispatchApp]);

    const handleImportAnnotations = useCallback(async event => {
        const file = event.target?.files?.[0];
        event.target.value = '';

        if (!file) {
            return;
        }

        if (!checkIfOnline(server, dispatchApp)) {
            return;
        }

        setImportState({ inProgress: true, progress: 0 });

        try {
            const fileText = await file.text();
            const parsedData = JSON.parse(fileText);
            const payload = normalizeImportedGeoJsonPayload(parsedData);

            await axios.post(`${server.serverHost}/api/annotations/array`, payload, {
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
                headers: { 'Authorization': `Basic ${server.token}` },
                onUploadProgress: progressEvent => {
                    if (progressEvent.total) {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setImportState({ inProgress: true, progress });
                    } else {
                        setImportState(prev => ({ ...prev, progress: prev.progress > 0 ? prev.progress : 5 }));
                    }
                }
            });

            setImportState({ inProgress: false, progress: 100 });
            dispatchServer({ type: 'SET_UPDATE_ANNOTATIONS' });
            dispatchApp({
                type: 'SHOW_SNACKBAR_MESSAGE',
                payload: { message: `${payload.length} annotation GeoJSON object(s) imported successfully.`, severity: 'success' }
            });
        } catch (error) {
            let errorMessage = `Import failed: ${error.message}`;

            if (axios.isAxiosError(error)) {
                errorMessage = error?.response?.data?.message || error?.response?.data?.error || error.message;
            }

            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: errorMessage, severity: 'error' } });
            setImportState({ inProgress: false, progress: 0 });
            return;
        }

        setImportState({ inProgress: false, progress: 0 });
    }, [server, dispatchApp, dispatchServer]);

    const actions = getTableActions(annotationData, selectedRow, setCreateAnnotationDlgOpen, requestDeleteConfirmation, openImportDialog, importState.inProgress, dispatchApp, server, toGeoJson);
    const columns = getColumns(handleHideCheckboxChange);

    if (!hasServerCredentials) {
        return <LinearProgress />;
    }

    return (
        <div className={classes.root}>
            <>
                <MaterialTable className={`${classes.root} ${classes.denseTable}`}
                    icons={tableIcons}
                    columns={columns}
                    data={annotationData || []}
                    title={<AnnotationTableToolbar data={annotationData} filter={filter} onSelection={handleToolbarSelection} />}
                    onRowClick={handleSelectedRow}
                    options={{
                        sorting: true,
                        exportButton: annotationData.length > 0,
                        exportFileName: `Annotations_${new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '-')}`,
                        cellStyle: { ...tableTheme.cellStyle, padding: '0.2em', fontSize: '0.9rem' },
                        headerStyle: { ...tableTheme.headerStyle, fontWeight: 'bold' },
                        filterRowStyle: tableTheme.filterRowStyle,
                        selection: true,
                        rowStyle: rowData => ({ ...tableTheme.rowStyle(rowData), cursor: 'pointer' }),
                        pageSize: 5,
                        pageSizeOptions: [5, 10, 20, 50, 100, 1000], // Common page size options      
                        emptyRowsWhenPaging: false, // Don't show empty rows
                    }}
                    actions={actions}
                    components={{
                        Toolbar: props => (
                            <div>
                                <MTableToolbar {...props} />
                            </div>
                        ),
                        Pagination: PatchedPagination,
                    }}
                />
                <input
                    ref={importFileInputRef}
                    type="file"
                    accept=".geojson,.json,application/json,application/geo+json,application/vnd.geo+json"
                    onChange={handleImportAnnotations}
                    style={{ display: 'none' }}
                />
                {createAnnotationDlgOpen.open && <CreateAnnotationDlg mode={createAnnotationDlgOpen.mode} annotationId={selectedRow?._id} lastMapCenter={props.lastMapCenter} onClose={handleAnnotationDlgClose} />}
                <Dialog
                    open={deleteConfirmState.open}
                    onClose={closeDeleteConfirmation}
                    aria-labelledby="delete-annotations-confirm-title"
                    aria-describedby="delete-annotations-confirm-description"
                >
                    <DialogTitle id="delete-annotations-confirm-title">Delete annotation{deleteConfirmState.annotations.length > 1 ? 's' : ''}?</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="delete-annotations-confirm-description">
                            {`Are you sure you want to delete ${deleteConfirmState.annotations.length} annotation${deleteConfirmState.annotations.length > 1 ? 's' : ''}? This action cannot be undone.`}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDeleteConfirmation} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={confirmDeleteAnnotations} color="secondary" autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
                {importState.inProgress && <LinearProgress className={classes.importProgress} variant="determinate" value={importState.progress} />}
                {isLoading && <LinearProgress />}
            </>
        </div>
    );
}

AnnotationTable.propTypes = {
    server: PropTypes.shape({
        serverHost: PropTypes.string.isRequired,
        token: PropTypes.string.isRequired,
        annotationsUpdate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    selectedAnnotationId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
    mapBoundingBox: PropTypes.object,
    onAnnotationSelected: PropTypes.func,
    onAnnotationSelectionUpdated: PropTypes.func,
};

AnnotationTable.defaultProps = {
    server: null,
};

export default AnnotationTable;
